'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, ExternalLink, ImageOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import { usePanelWidth } from '@/hooks/use-panel-width';
import { Link } from '@/i18n/routing';
import { API_ENDPOINTS } from '@/lib/api/config';
import type { ContainerPageRow } from '../utils/container-page-rows';
import { resolvePageRowState } from '../utils/page-row-state';
import { partitionFields } from '../utils/partition-fields';
import { resultLocalPageOf } from '../utils/result-local-page';
import { resultsPageHash } from '../utils/results-deep-link';
import { DocumentPagesViewer, type ViewerPage } from './document-pages-viewer';
import { ExtractedFieldCard } from './extracted-field-card';
import { ContainerIdPrefix } from './container-id-prefix';
import type { ExtractedDataItem } from '../types';

/**
 * The card demands them even read-only, where both affordances are hidden and
 * neither can fire. Accepting or correcting a value stays on the full results
 * page, where the operator has the whole document in front of them.
 */
function noop() {}

/**
 * One field as this dialog addresses it.
 *
 * `${key}-${page}` is NOT a unique identity: one page can carry the same key
 * twice — a repeating template occurrence, or a free-form item that
 * rediscovered a declared name — which React reports as two children sharing
 * the key `شروط_الدفع`. The position inside its own bucket is what makes an
 * entry distinct, and it doubles as the selection identity.
 */
interface FieldEntry {
  id: string;
  field: ExtractedDataItem;
}

interface PageResultsDialogProps {
  /** The page under review; null keeps the dialog closed. */
  row: ContainerPageRow | null;
  /** Null only while the sheet is closed, where this never renders. */
  containerId: number | null;
  containerLabel: string;
  /** Physical pages in the container — the "page N of M" orientation line. */
  totalPages: number;
  onClose: () => void;
}

/**
 * One page's scan beside the values read off it, opened from a row of the
 * container-results sheet.
 *
 * Read-only on purpose: an operator checking a single value against the
 * document should not have to leave the containers listing, but accepting or
 * correcting one needs the whole document, which is what the results page is
 * for. The footer link goes there, landing on this same page.
 *
 * Local `useState`, NOT a URL-hash overlay. The sheet that opens this is itself
 * hash-driven (`#results/<id>`) and only one hash overlay can be open at a
 * time, so hash-wiring this would clobber its parent's key and close the sheet
 * underneath it. The documented trade-off is that browser Back closes the sheet
 * rather than this dialog — see `features/CLAUDE.md` § URL-Hash Overlays.
 */
export function PageResultsDialog({
  row,
  containerId,
  containerLabel,
  totalPages,
  onClose
}: PageResultsDialogProps) {
  return (
    <Dialog
      open={row != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className='flex h-[96dvh] max-h-[96dvh] w-[98vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[110rem]'>
        {row && (
          // Keyed by page id so the selected field resets when the operator
          // opens a different page from the sheet still standing behind this.
          <PageResultsBody
            key={row.page.id}
            row={row}
            containerId={containerId}
            containerLabel={containerLabel}
            totalPages={totalPages}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PageResultsBody({
  row,
  containerId,
  containerLabel,
  totalPages
}: Omit<PageResultsDialogProps, 'row' | 'onClose'> & {
  row: ContainerPageRow;
}) {
  const t = useTranslations('ocr');
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  // Its own storage key: the results page's extraction panel sits beside a
  // whole document, this one beside a single page, so one width cannot suit
  // both.
  const fieldsPanelResize = usePanelWidth('ocr-page-results', {
    defaultWidth: 340,
    min: 280,
    max: 640,
    // Sits at the split's inline end, so its handle is on its inline-start
    // edge — physically left in LTR, right in RTL.
    edge: 'start'
  });

  const { result, page, pageIndex, fields } = row;
  const rowState = resolvePageRowState(row);
  const pageLabel = t('pageLabel', { number: page.page_number ?? pageIndex });

  // A chunk's `page_number` is 1-based within the OWNING RESULT's page list,
  // not the container's, so the container-wide `pageIndex` would attach this
  // page's highlights to a page the result does not have — and nothing would
  // be outlined at all.
  const localPageNumber = resultLocalPageOf(result, page.id, pageIndex);

  const { templateEntries, freeformEntries, fullPageField } = useMemo(() => {
    const { template, freeform, fullPage } = partitionFields(fields);
    const identify =
      (bucket: string) => (field: ExtractedDataItem, index: number) => ({
        id: `${bucket}-${index}-${field.key}`,
        field
      });

    return {
      templateEntries: template.map(identify('template')),
      freeformEntries: freeform.map(identify('freeform')),
      // A `full_page` item is not a verifiable field — no references, no accept
      // affordance, and it can never reach `processed` — so it is kept out of
      // the counters below, exactly as the results panel keeps it out of its
      // own.
      fullPageField: fullPage[0] ?? null
    };
  }, [fields]);

  const verifiable: FieldEntry[] = useMemo(
    () => [...templateEntries, ...freeformEntries],
    [templateEntries, freeformEntries]
  );
  const verifiedCount = verifiable.filter(
    (entry) => entry.field.status === 'processed'
  ).length;

  const refsOf = useCallback(
    (id: string | null): string[] => {
      if (id == null) return [];
      const entry = verifiable.find((candidate) => candidate.id === id);

      // An ungrounded field resolves to no region, which is the honest answer:
      // the card flags it, and nothing lights up on the page.
      return entry?.field.references ?? [];
    },
    [verifiable]
  );

  const viewerPages: ViewerPage[] = useMemo(() => {
    // Local disk emits `image_url: null` (only s3/gcs/public get signed), so
    // route through the authenticated streaming endpoint whenever the page
    // reports an image — the same rule the results page applies.
    const authEndpoint =
      result.batch_id != null && (page.image_url || page.has_image)
        ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(result.batch_id, page.id)
        : null;
    if (!authEndpoint && !page.image_url) return [];

    const entries = [...templateEntries, ...freeformEntries];
    const referenced = new Set(
      entries.flatMap((entry) => entry.field.references ?? [])
    );
    const colorByRef = new Map(
      entries.flatMap((entry) =>
        (entry.field.references ?? []).map(
          (ref) => [ref, entry.field.color ?? null] as const
        )
      )
    );

    // Only the regions a field was read from — handing the viewer every chunk
    // on the page outlines paragraphs nobody extracted anything from.
    const chunks = (result.chunks ?? [])
      .filter(
        (chunk) =>
          chunk.page_number === localPageNumber &&
          referenced.has(chunk.chunk_id)
      )
      .map((chunk) => ({
        ...chunk,
        color: colorByRef.get(chunk.chunk_id) ?? null
      }));

    return [
      {
        imageUrl: authEndpoint ? null : page.image_url,
        authEndpoint,
        label: pageLabel,
        chunks,
        isProcessing:
          result.status === 'processing' || result.status === 'pending'
      }
    ];
  }, [
    result,
    page,
    localPageNumber,
    templateEntries,
    freeformEntries,
    pageLabel
  ]);

  const handleChunkClick = useCallback(
    (chunkId: string) => {
      const owner = verifiable.find((entry) =>
        (entry.field.references ?? []).includes(chunkId)
      );
      if (owner) setActiveFieldId(owner.id);
    },
    [verifiable]
  );

  const renderCard = (entry: FieldEntry) => (
    <ExtractedFieldCard
      key={entry.id}
      field={entry.field}
      isActive={activeFieldId === entry.id}
      isHighlighted={hoveredFieldId === entry.id}
      readOnly
      onHover={() => setHoveredFieldId(entry.id)}
      onClick={() => setActiveFieldId(entry.id)}
      onAccept={noop}
      onUpdate={noop}
    />
  );

  const hasAnyData = verifiable.length > 0 || fullPageField != null;

  return (
    <>
      <DialogHeader className='shrink-0 space-y-1 border-b px-5 py-3 text-start'>
        <DialogTitle className='flex min-w-0 flex-wrap items-center gap-x-2 text-sm'>
          <span className='truncate'>{pageLabel}</span>
          <span className='flex min-w-0 items-center gap-1.5 font-normal'>
            <ContainerIdPrefix id={containerId} />
            <span dir='auto' className='text-muted-foreground truncate'>
              {containerLabel}
            </span>
          </span>
          <span className='text-muted-foreground text-[11px] font-normal'>
            {t('pageOfPages', { current: pageIndex, total: totalPages })}
          </span>
        </DialogTitle>
        <DialogDescription className='text-xs'>
          {t('pageResultsHint')}
        </DialogDescription>
      </DialogHeader>

      {/* Resizable split (OMA-672): the document pane takes whatever the field
          column leaves, and the column starts at the results panel's ~340px but
          can be dragged wider for long values or narrower for a dense scan.
          Stacks to one column below `lg`, where the handle hides itself. */}
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row'>
        <div className='min-h-0 flex-1 p-2'>
          {viewerPages.length === 0 ? (
            <div className='text-muted-foreground flex h-full flex-col items-center justify-center gap-2 text-sm'>
              <ImageOff className='h-6 w-6 opacity-50' />
              {t('pageImageUnavailable')}
            </div>
          ) : (
            // No editing props: no bbox drag, no draw mode, no colour menu, no
            // page rotation, no rearrange. `pdfUrl` is deliberately omitted too
            // — the result's PDF is the whole container, which is not what this
            // dialog is showing.
            <DocumentPagesViewer
              pages={viewerPages}
              pdfUrl={null}
              highlightedChunkIds={refsOf(hoveredFieldId)}
              activeChunkIds={refsOf(activeFieldId)}
              activePage={0}
              onChunkClick={handleChunkClick}
            />
          )}
        </div>

        <PanelResizeHandle {...fieldsPanelResize.handleProps} />
        <div
          className='flex min-h-0 flex-1 flex-col overflow-hidden border-t lg:w-(--page-fields-width) lg:flex-none lg:border-s lg:border-t-0'
          style={
            {
              // Distinct from the hook's own --panel-ocr-page-results-width:
              // a property cannot reference itself, CSS drops it as a cycle.
              '--page-fields-width': fieldsPanelResize.widthValue
            } as React.CSSProperties
          }
          // The card sets hover but never clears it (clearing per-card makes
          // the viewer flicker as the cursor crosses the gap between two), so
          // the list clears it on the way out.
          onPointerLeave={() => setHoveredFieldId(null)}
        >
          <div className='shrink-0 space-y-0.5 px-4 py-2.5'>
            <p className='text-xs font-semibold uppercase'>
              {t('extractedData')}
            </p>
            {verifiable.length > 0 ? (
              <p className='text-muted-foreground text-[11px]'>
                {verifiedCount}/{verifiable.length} {t('fields')}{' '}
                {t('statusVerified').toLowerCase()} &middot;{' '}
                {t('selectFieldToLocate')}
              </p>
            ) : null}
          </div>
          <Separator />

          <div className='min-w-0 flex-1 space-y-2 overflow-y-auto px-4 py-3'>
            {!hasAnyData ? (
              <div className='space-y-1 py-6 text-center'>
                <p className='text-muted-foreground text-sm'>
                  {t('noExtractedData')}
                </p>
                {/* Three states, not one — the same distinction the row that
                    opened this dialog draws, in the same words. */}
                {(rowState === 'no_data' || rowState === 'not_covered') && (
                  <p className='text-muted-foreground text-xs'>
                    {rowState === 'no_data'
                      ? t('pageReadNoData')
                      : t('pageNotCoveredHint')}
                  </p>
                )}
              </div>
            ) : (
              <>
                {templateEntries.map(renderCard)}

                {freeformEntries.length > 0 && (
                  <div className='space-y-2 pt-1'>
                    <Separator />
                    <div className='space-y-0.5'>
                      <p className='text-xs font-semibold uppercase'>
                        {t('freeformSectionTitle')}
                      </p>
                      <p className='text-muted-foreground text-[11px]'>
                        {t('freeformSectionHint')}
                      </p>
                    </div>
                    {freeformEntries.map(renderCard)}
                  </div>
                )}

                {/* Collapsed by default: it is the tallest thing here, and this
                    column is a fraction of the panel's height. The chevron is
                    scoped with `group-data-[state=open]`, never the unscoped
                    ancestor form — any nested Collapsible in this feature that
                    used the latter matched the wrong element's state. */}
                {fullPageField && (
                  <Collapsible className='group pt-1'>
                    <Separator className='mb-2' />
                    <CollapsibleTrigger className='text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 text-xs font-semibold uppercase'>
                      <ChevronDown className='h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180' />
                      {t('fullPageSectionTitle')}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      {/* `dir='auto'` plus `[unicode-bidi:plaintext]` — the dump
                          carries the DOCUMENT's direction, per line: a Latin
                          value inheriting the Arabic shell's RTL has its leading
                          numeric run moved to the far end, so a stored
                          "90 days from invoice date" reads back as
                          "days from invoice date 90". */}
                      <pre
                        dir='auto'
                        className={cn(
                          'bg-muted/40 mt-2 max-h-64 overflow-auto rounded-md border p-2.5 text-xs',
                          'whitespace-pre-wrap [unicode-bidi:plaintext]'
                        )}
                      >
                        {String(fullPageField.value ?? '')}
                      </pre>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {containerId != null && (
        <div className='flex shrink-0 items-center justify-end border-t px-4 py-2.5'>
          <Button variant='outline' size='sm' className='h-7 text-xs' asChild>
            <Link
              href={`/dashboard/ocr-containers/${containerId}#${resultsPageHash(pageIndex)}`}
            >
              {t('openResultsPage')}
              <ExternalLink className='ms-1.5 h-3 w-3 opacity-50' />
            </Link>
          </Button>
        </div>
      )}
    </>
  );
}
